import { useState } from 'react';

import { Heading, Modal, Paragraph } from '@zillow/constellation-10';

import { useRentalManagerUserClaimWebApiHandler_deleteAllUnverified } from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import { isEmailInContactMethods, isPhoneInContactMethods } from '../../../utils/listingClaimUtils';

import ClaimListingCard from '../../ClaimListingCard';
import ModalFooter from '../ModalFooter';

import type { UserClaimGetExclusivePropertiesForAllUnverifiedListingDto } from '@zg-rentals/ts-rental-manager-api-beta-client';

export type ListingRemovalModalProps = {
  onSubmitSuccess: () => void;
  onClose: () => void;
  listings: Array<UserClaimGetExclusivePropertiesForAllUnverifiedListingDto>;
  contactMethods: { emails: Array<string | { value: string }>; phones: Array<string | { value: string }> };
};

const ListingRemovalModal = ({ onSubmitSuccess, onClose, listings, contactMethods }: ListingRemovalModalProps) => {
  const { mutateAsync: deleteAllUnverified, isPending: isSubmitting } =
    useRentalManagerUserClaimWebApiHandler_deleteAllUnverified();
  const [hasError, setHasError] = useState(false);

  const handleSubmitClick = async () => {
    setHasError(false);
    try {
      await deleteAllUnverified();
      onSubmitSuccess();
    } catch {
      setHasError(true);
    }
  };

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      size={{ base: 'fullScreen', md: 'md' }}
      header={<Heading level={2}>Listing removal</Heading>}
      body={
        <>
          <Paragraph css={{ marginBottom: '400' }}>
            The following listings will be removed due to unverified contact methods. You can always come back to this
            tool to add them back in.
          </Paragraph>
          {listings.map((listing) => (
            <ClaimListingCard
              key={listing?.listingAlias}
              listingData={listing}
              highlightPhone={isPhoneInContactMethods(contactMethods, listing?.contactPhone)}
              highlightEmail={isEmailInContactMethods(contactMethods, listing?.contactEmail)}
            />
          ))}
        </>
      }
      footer={
        <ModalFooter
          cancelText="Back to verify"
          submitText="Remove"
          onCancel={onClose}
          onSubmit={() => handleSubmitClick()}
          isSubmitting={isSubmitting}
          hasError={hasError}
        />
      }
    />
  );
};

export default ListingRemovalModal;
